import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { adminPartnersClient } from '@/lib/api'
import {
  DEFAULT_PARTNER_SCOPES,
  VISIBLE_INTEGRATIONS,
} from '@super/shared-types'

const createPartnerSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  email: z.string().email('Valid email is required'),
  integrationType: z.string().min(1, 'Integration type is required'),
})

type FormData = z.infer<typeof createPartnerSchema>

// The integration types a partner can be created for, from shared-types
const INTEGRATION_TYPES = VISIBLE_INTEGRATIONS.map(i => ({
  value: i.TYPE,
  label: i.NAME,
}))

interface CreatePartnerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function CreatePartnerDialog({
  open,
  onOpenChange,
  onSuccess,
}: CreatePartnerDialogProps) {
  const [creating, setCreating] = useState(false)

  const form = useForm<FormData>({
    resolver: zodResolver(createPartnerSchema),
    defaultValues: {
      name: '',
      email: '',
      integrationType: '',
    },
  })

  // Reset form when dialog opens or closes
  useEffect(() => {
    if (!open) {
      form.reset()
    }
  }, [open, form])

  const onSubmit = async (data: FormData) => {
    try {
      setCreating(true)
      await adminPartnersClient.createPartner({
        name: data.name,
        contactEmail: data.email,
        integrationType: data.integrationType as any, // Type assertion for integration type
        scopes: DEFAULT_PARTNER_SCOPES,
        inviteEmail: data.email,
      })
      form.reset()
      onSuccess()
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to create partner',
      )
    } finally {
      setCreating(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="mb-3">Create Partner Account</DialogTitle>
          <DialogDescription className="px-3">
            Create a new partner account with WorkOS organization and
            OAuth&nbsp;credentials.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="name">Partner name</FormLabel>
                  <FormControl>
                    <Input id="name" placeholder="Acme, Inc." {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="email">Email</FormLabel>
                  <FormControl>
                    <Input
                      id="email"
                      type="email"
                      placeholder="tech@partner.com"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription>
                    We'll send a WorkOS invitation to this email
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="integrationType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel htmlFor="integrationType">
                    Integration type
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select integration type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {INTEGRATION_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={creating}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={creating}>
                {creating ? 'Creating...' : 'Create partner'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
